import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './TrendAnalysis.css';
import violationDescriptions from '../../utils/violationDescriptions';
import tooltipTexts from '../../utils/tooltipTexts';

const TrendAnalysis = ({ vehicleId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    console.log('TrendAnalysis - vehicleId changed:', vehicleId);
    
    // Create an AbortController for cancelling previous requests
    const controller = new AbortController();
    const signal = controller.signal;
    
    // Reset state when vehicleId changes to show loading state
    setIsLoading(true);
    
    const fetchData = async () => {
      try {
        // Use the new endpoint that includes descriptions
        const url = vehicleId 
          ? `http://eframeehs.in:3001/api/violation-percentage-with-descriptions?vehicleId=${vehicleId}`
          : 'http://eframeehs.in:3001/api/violation-percentage-with-descriptions';
          
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setTableData(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
    
    // Cleanup function to cancel any in-flight requests when vehicleId changes
    return () => {
      console.log('TrendAnalysis - Aborting previous fetch if any');
      controller.abort();
    };
  }, [vehicleId]);

  if (isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3>Trend Analysis / Challenges
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.trendAnalysis}</span>
            </span>
          </h3>
        </div>
        <div className="loading-container">
          Loading trend data...
        </div>
      </div>
    );
  }

  if (!tableData.length) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3>Trend Analysis / Challenges
            <span className="info-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.trendAnalysis}</span>
            </span>
          </h3>
        </div>
        <div className="loading-container">
          No data available
        </div>
      </div>
    );
  }

  // Find the maximum percentage for scaling
  const maxPercentage = Math.max(...tableData.map(item => parseFloat(item.ViolationPercentage)));
  
  // Sort data by percentage in descending order for better visual representation
  const sortedData = [...tableData].sort((a, b) => 
    parseFloat(b.ViolationPercentage) - parseFloat(a.ViolationPercentage)
  );

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>Trend Analysis / Challenges
          <span className="info-tooltip">
            <span className="info-icon">i</span>
            <span className="tooltip-text">{tooltipTexts.trendAnalysis}</span>
          </span>
        </h3>
      </div>
      <div className="trend-table-container">
        <table className="trend-table">
          <thead>
            <tr>
              <th>Violations</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => {
              const percentage = parseFloat(item.ViolationPercentage);
              // Ensure minimum width for tiny percentages to make them visible
              const barWidth = Math.max((percentage / maxPercentage) * 100, 5); 
              // Use the description from the API or fallback to the imported map
              const description = item.ViolationDescription || violationDescriptions[item.ViolationScreenName] || '';
              
              // Use a single class for all bars
              const barClass = "percentage-bar";
              
              // Determine label position based on percentage value
              let labelClass = "bar-label";
              if (percentage < 10) labelClass += " outside-label";
              
              return (
                <tr key={index}>
                  <td>
                    <div className="violation-name-container">
                      <span className="violation-tooltip" data-tooltip={description}>
                        {item.ViolationScreenName}
                        {/* <span className="info-icon">ⓘ</span> */}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="bar-container">
                      <div 
                        className={barClass}
                        style={{ width: `${barWidth}%` }}
                      >
                        {percentage > 0 && (
                          <span className={labelClass}>{percentage.toFixed(1)}%</span>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TrendAnalysis.propTypes = {
  vehicleId: PropTypes.string
};

export default TrendAnalysis;